import React, { useState, useEffect } from "react";
import { useCart } from "./CartContext";
import "./CartDrawer.css";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import donationImage from "../../images/donation_box.png";
import FreeShippingProgressBar from "./FreeShippingProgressBar";

const CartDrawer: React.FC = () => {
  const {
    state,
    dispatch,
    isDrawerOpen,
    toggleDrawer,
    subtotal,
    totalItems,
    freeShippingThreshold,
  } = useCart();
  const { cart } = state;
  const navigate = useNavigate();
  const DONATION_ID = config.DONATION_ITEM_ID;
  const BUYOUT_ID = config.BUYOUT_ITEM_ID;

  const [errorMessage, setErrorMessage] = useState("");

  const handleProceedToCheckOut = () => {
    if (cart.length === 0) {
      setErrorMessage("Your cart is empty. Add items to proceed to checkout.");
    } else {
      setErrorMessage("");
      navigate("/checkout/cart");
      toggleDrawer();
    }
  };

  const addItemToCart = (item: any) => {
    dispatch({ type: "ADD_ITEM", payload: item });
  };

  const handleQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    itemId: string
  ) => {
    const newQuantity = Number(e.target.value);
    if (newQuantity >= 1) {
      dispatch({
        type: "UPDATE_ITEM_QUANTITY",
        payload: { id: itemId, quantity: newQuantity },
      });
    }
  };

  const increaseQuantity = (item: any) => {
    dispatch({
      type: "UPDATE_ITEM_QUANTITY",
      payload: { id: item.id, quantity: item.quantity + 1 },
    });
  };

  const decreaseQuantity = (item: any) => {
    if (item.quantity > 1) {
      dispatch({
        type: "UPDATE_ITEM_QUANTITY",
        payload: { id: item.id, quantity: item.quantity - 1 },
      });
    }
  };

  const handleCloseDrawer = () => {
    setErrorMessage("");
    toggleDrawer();
  };

  const donation = cart.reduce((sum, item) => {
    if (item.sku === DONATION_ID || item.sku === BUYOUT_ID) {
      return sum + item.price * item.quantity;
    }
    return sum;
  }, 0);
  const price = subtotal - donation;

  return (
    <>
      {/* Overlay for closing the drawer */}
      {isDrawerOpen && (
        <div className="cart-overlay" onClick={handleCloseDrawer}></div>
      )}

      <div className={`cart-drawer ${isDrawerOpen ? "open" : ""}`}>
        <div className="cart-drawer-header">
          <p className="cart-header-item-count-display">Items: {totalItems}</p>

          <button onClick={handleCloseDrawer} className="close-drawer-btn">
            X
          </button>
        </div>
        <div className="cart-drawer-content">
          {cart.length === 0 ? (
            <p>Your cart is empty</p>
          ) : (
            <ul>
              {cart.map((item: any, index: any) => {
                const productImage =
                  item.image && item.image.trim()
                    ? `${item.image}`
                    : donationImage;

                return (
                  <li key={index} className="cart-item">
                    <img
                      src={`${productImage}`}
                      alt={item.name}
                      className="cart-item-image"
                    />
                    <div className="cart-item-info">
                      <span className="cart-item-name">{item.name}</span>
                      <span className="cart-item-price">
                        ${item.price.toFixed(2)}
                      </span>
                      <div className="cart-item-actions">
                        <div className="quantity-controls">
                          <button
                            onClick={() => decreaseQuantity(item)}
                            className="quantity-btn"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            className="quantity-display"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(e, item.id)}
                            min="1"
                          />
                          <button
                            onClick={() => increaseQuantity(item)}
                            className="quantity-btn"
                          >
                            +
                          </button>
                        </div>
                        <button
                          className="remove-item-btn"
                          onClick={() =>
                            dispatch({ type: "REMOVE_ITEM", payload: item.id })
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="cart-drawer-footer">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <FreeShippingProgressBar
              current={price}
              max={freeShippingThreshold}
            />
          </div>
          {errorMessage && <p className="cart-error-message">{errorMessage}</p>}
          <div className="cart-drawer-subtotal-container">
            <p className="cart-drawer-subtotal-display">Subtotal</p>
            <p>${subtotal.toFixed(2)}</p>
          </div>
          <div className="cart-drawer-checkout-button-container">
            <button
              className="cart-drawer-checkout-button"
              onClick={handleProceedToCheckOut}
            >
              Checkout
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartDrawer;
